import { useEffect, useMemo, useState } from 'react';
import {
  createHeightRangeId,
  formatHeightRangeLabel,
  getAvailableHeightRanges,
  getParticipantsInHeightRange,
  validateHeightRanges,
} from '../../utils/bracketSet';
import { useI18n } from '../../i18n';

function BracketHeightRangeSelect({ bracketSet, onChange }) {
  const { t } = useI18n();
  const ranges = getAvailableHeightRanges(bracketSet);

  if (ranges.length === 0) {
    return (
      <p className="bracket-height-select-empty">{t('bracket.height.selectEmpty')}</p>
    );
  }

  return (
    <div className="bracket-height-select">
      <label className="form-label" htmlFor="bracket-height-filter">
        {t('bracket.height.selectLabel')}
      </label>
      <select
        id="bracket-height-filter"
        className="form-select bracket-height-select-input"
        value={bracketSet.selectedHeightRangeId ?? ranges[0].id}
        onChange={(e) => onChange(e.target.value)}
      >
        {ranges.map((range) => (
          <option key={range.id} value={range.id}>
            {range.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function BracketHeightRangeEditor({ heightRanges, participants, onApply }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState(heightRanges);

  useEffect(() => {
    setDraft(
      heightRanges.length > 0
        ? heightRanges
        : [{ id: createHeightRangeId(), min: '', max: '' }]
    );
  }, [heightRanges]);

  const previewCounts = useMemo(() => {
    const validation = validateHeightRanges(
      draft.map((range) => ({
        ...range,
        min: range.min === '' ? NaN : Number(range.min),
        max: range.max === '' ? NaN : Number(range.max),
      }))
    );
    if (!validation.valid) return {};

    return validation.ranges.reduce((counts, range) => {
      counts[range.id] = getParticipantsInHeightRange(participants, range).length;
      return counts;
    }, {});
  }, [draft, participants]);

  const updateRange = (id, field, value) => {
    setDraft((prev) =>
      prev.map((range) => (range.id === id ? { ...range, [field]: value } : range))
    );
  };

  const addRange = () => {
    setDraft((prev) => [...prev, { id: createHeightRangeId(), min: '', max: '' }]);
  };

  const removeRange = (id) => {
    setDraft((prev) => (prev.length <= 1 ? prev : prev.filter((range) => range.id !== id)));
  };

  const handleApply = () => {
    const validation = validateHeightRanges(draft);
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    const insufficient = validation.ranges.filter(
      (range) => getParticipantsInHeightRange(participants, range).length < 2
    );
    if (insufficient.length === validation.ranges.length) {
      alert(t('bracket.height.noValidRange'));
      return;
    }

    if (insufficient.length > 0) {
      const labels = insufficient
        .map((range) => formatHeightRangeLabel(range.min, range.max))
        .join(', ');
      const confirmed = window.confirm(t('bracket.height.confirmInsufficient', { labels }));
      if (!confirmed) return;
    }

    onApply(validation.ranges);
  };

  return (
    <div className="bracket-height-editor">
      <div className="bracket-height-editor-header">
        <span className="bracket-height-editor-title">{t('bracket.height.editorTitle')}</span>
        <button type="button" className="btn btn-height-add" onClick={addRange}>
          {t('bracket.height.addRange')}
        </button>
      </div>
      <p className="bracket-height-editor-hint">{t('bracket.height.editorHint')}</p>
      <div className="bracket-height-range-list">
        {draft.map((range) => (
          <div key={range.id} className="bracket-height-range-row">
            <label className="bracket-height-range-field">
              <span className="bracket-height-range-label">{t('bracket.height.min')}</span>
              <input
                type="number"
                className="form-input bracket-height-input"
                min="1"
                max="250"
                placeholder="130"
                value={range.min}
                onChange={(e) => updateRange(range.id, 'min', e.target.value)}
              />
            </label>
            <span className="bracket-height-range-sep">~</span>
            <label className="bracket-height-range-field">
              <span className="bracket-height-range-label">{t('bracket.height.max')}</span>
              <input
                type="number"
                className="form-input bracket-height-input"
                min="1"
                max="250"
                placeholder="145"
                value={range.max}
                onChange={(e) => updateRange(range.id, 'max', e.target.value)}
              />
            </label>
            {previewCounts[range.id] != null && (
              <span className="bracket-height-range-count">{t('common.people', { count: previewCounts[range.id] })}</span>
            )}
            <button
              type="button"
              className="btn btn-height-remove"
              onClick={() => removeRange(range.id)}
              disabled={draft.length <= 1}
              aria-label={t('bracket.height.removeRangeAria')}
            >
              {t('bracket.height.remove')}
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-height-apply" onClick={handleApply}>
        {t('bracket.height.apply')}
      </button>
    </div>
  );
}

function BracketHeightRangePanel({ bracketSet, participants, editMode, onApplyRanges, onSelectRange }) {
  return (
    <div className="bracket-height-panel">
      {editMode && (
        <BracketHeightRangeEditor
          heightRanges={bracketSet.heightRanges ?? []}
          participants={participants}
          onApply={onApplyRanges}
        />
      )}
      <BracketHeightRangeSelect bracketSet={bracketSet} onChange={onSelectRange} />
    </div>
  );
}

export default BracketHeightRangePanel;
